import { useNavigate } from 'react-router-dom';
import {
  PartyPopper, CheckCircle2, User, Server, Network, Search, Monitor,
  Link2, Shield, Video, DoorOpen, Settings, ChevronRight, GraduationCap
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import useTutorialStore from '../../../stores/tutorialStore';
import { useBranding } from '../../../contexts/BrandingContext';
import liveViewTutorial from '../../tutorials/configs/liveViewTutorial';
import monitoringTutorial from '../../tutorials/configs/monitoringTutorial';
import accessControlTutorial from '../../tutorials/configs/accessControlTutorial';
import recordingAssuranceTutorial from '../../tutorials/configs/recordingAssuranceTutorial';
import settingsTutorial from '../../tutorials/configs/settingsTutorial';

const VMS_TYPES = ['WAVE_VMS', 'GENETEC_VMS', 'MILESTONE_VMS', 'MIRASYS_VMS', 'AVIGILON_VMS', 'VERKADA_VMS'];
const ACS_TYPES = ['FEENICS_ACCESS', 'GENETEC_ACS', 'BRIVO_ACCESS', 'OPENPATH_ACCESS', 'AXIS_VAPIX'];

const CompletionStep = () => {
  const navigate = useNavigate();
  const { savedData, prerequisites, completeWizard } = useSiteSetupStore();
  const { branding } = useBranding();
  const brandName = branding.brandName;

  const profile = savedData?.profile || {};
  const profileName = [profile.firstName, profile.lastName].filter(Boolean).join(' ') || 'Complete';
  const orgName = profile.orgName || profile.organizationName || '';

  const agents = prerequisites?.agents || [];
  const agent = agents[0] || null;

  const networks = savedData?.networks || [];
  const deviceCount = savedData?.deviceCount ?? prerequisites?.discoveredDeviceCount ?? 0;
  const monitoredCount = savedData?.promoted ?? 0;
  const monitoringSkipped = !!savedData?.skipped && monitoredCount === 0;

  const configuredIntegrations = savedData?.configuredIntegrations || savedData?.selectedIntegrations || [];
  const hasVms = configuredIntegrations.some(t => VMS_TYPES.includes(t));
  const hasAcs = configuredIntegrations.some(t => ACS_TYPES.includes(t));

  const raCameras = savedData?.cameraCount ?? 0;
  const raEnabled = !!savedData?.enabled && raCameras > 0;

  const handleComplete = async () => {
    await completeWizard();
  };

  const go = (path) => async () => {
    await completeWizard();
    navigate(path);
  };

  const goAndTour = (path, tutorial) => async () => {
    await completeWizard();
    navigate(path);
    setTimeout(() => {
      useTutorialStore.getState().startTutorial(tutorial.module, tutorial.steps);
    }, 600);
  };

  return (
    <div className="site-setup-step completion-step">
      <div className="completion-hero">
        <div className="completion-hero-icon">
          <PartyPopper size={40} />
        </div>
        <h1>Your site is ready!</h1>
        <p>
          {brandName} is set up and monitoring. Here's a snapshot of what was configured.
        </p>
      </div>

      <div className="completion-summary-grid">
        <div className="completion-summary-card">
          <User size={16} />
          <div>
            <strong>Profile</strong>
            <span>{profileName}{orgName ? ` · ${orgName}` : ''}</span>
          </div>
        </div>

        <div className="completion-summary-card">
          <Server size={16} />
          <div>
            <strong>Agent</strong>
            <span>
              {agent
                ? `${agent.hostname || agent.name || 'Connected'}${agent.version ? ` · v${agent.version}` : ''}`
                : 'Connected'}
            </span>
          </div>
        </div>

        <div className="completion-summary-card">
          <Network size={16} />
          <div>
            <strong>Network</strong>
            <span>{networks.length > 0 ? networks.join(', ') : 'Configured'}</span>
          </div>
        </div>

        <div className="completion-summary-card">
          <Search size={16} />
          <div>
            <strong>Devices Discovered</strong>
            <span>{deviceCount} device{deviceCount === 1 ? '' : 's'}</span>
          </div>
        </div>

        <div className="completion-summary-card">
          <Monitor size={16} />
          <div>
            <strong>Monitoring</strong>
            <span>
              {monitoringSkipped
                ? 'Skipped'
                : `${monitoredCount} device${monitoredCount === 1 ? '' : 's'} monitored`}
            </span>
          </div>
        </div>

        <div className="completion-summary-card">
          <Link2 size={16} />
          <div>
            <strong>Integrations</strong>
            <span>
              {configuredIntegrations.length > 0
                ? configuredIntegrations.map(t => t.replace(/_/g, ' ')).join(', ')
                : 'None'}
            </span>
          </div>
        </div>

        <div className="completion-summary-card">
          <Shield size={16} />
          <div>
            <strong>Recording Assurance</strong>
            <span>
              {raEnabled
                ? `Enabled for ${raCameras} camera${raCameras === 1 ? '' : 's'}`
                : hasVms ? 'Skipped' : 'Not applicable'}
            </span>
          </div>
        </div>
      </div>

      <div className="completion-actions">
        <p className="completion-actions-label">Where would you like to go next?</p>
        <div className="completion-quick-links">
          {hasVms && (
            <div className="completion-quick-link-row">
              <button className="site-setup-btn outline" onClick={go('/live')}>
                <Video size={14} /> Live Cameras
              </button>
              <button className="site-setup-btn ghost tour-link" onClick={goAndTour('/live', liveViewTutorial)}>
                <GraduationCap size={13} /> Take a Tour
              </button>
            </div>
          )}
          <div className="completion-quick-link-row">
            <button className="site-setup-btn outline" onClick={go('/monitoring')}>
              <Monitor size={14} /> Monitoring
            </button>
            <button className="site-setup-btn ghost tour-link" onClick={goAndTour('/monitoring', monitoringTutorial)}>
              <GraduationCap size={13} /> Take a Tour
            </button>
          </div>
          {hasAcs && (
            <div className="completion-quick-link-row">
              <button className="site-setup-btn outline" onClick={go('/access-control')}>
                <DoorOpen size={14} /> Access Control
              </button>
              <button className="site-setup-btn ghost tour-link" onClick={goAndTour('/access-control', accessControlTutorial)}>
                <GraduationCap size={13} /> Take a Tour
              </button>
            </div>
          )}
          {raEnabled && (
            <div className="completion-quick-link-row">
              <button className="site-setup-btn outline" onClick={go('/recording-assurance')}>
                <Shield size={14} /> Recording Assurance
              </button>
              <button className="site-setup-btn ghost tour-link" onClick={goAndTour('/recording-assurance', recordingAssuranceTutorial)}>
                <GraduationCap size={13} /> Take a Tour
              </button>
            </div>
          )}
          <div className="completion-quick-link-row">
            <button className="site-setup-btn outline" onClick={go('/settings')}>
              <Settings size={14} /> Settings
            </button>
            <button className="site-setup-btn ghost tour-link" onClick={goAndTour('/settings', settingsTutorial)}>
              <GraduationCap size={13} /> Take a Tour
            </button>
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button className="site-setup-btn primary large" onClick={handleComplete}>
          <CheckCircle2 size={16} /> Complete Setup <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default CompletionStep;
